import { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import { AdminUser, CreateUserRequest, UpdateUserRequest } from '../types/admin';

interface UseAdminUsersReturn {
  users: AdminUser[];
  loading: boolean;
  error: string | null;
  createUser: (data: CreateUserRequest) => Promise<void>;
  updateUser: (id: string, data: UpdateUserRequest) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  refreshUsers: () => Promise<void>;
}

export const useAdminUsers = (): UseAdminUsersReturn => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getUsers();
      setUsers(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const createUser = async (data: CreateUserRequest) => {
    try {
      setError(null);
      const newUser = await adminService.createUser(data);
      setUsers([...users, newUser]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create user';
      setError(message);
      throw err;
    }
  };

  const updateUser = async (id: string, data: UpdateUserRequest) => {
    try {
      setError(null);
      const updatedUser = await adminService.updateUser(id, data);
      setUsers(users.map(u => (u.id === id ? updatedUser : u)));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update user';
      setError(message);
      throw err;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      setError(null);
      await adminService.deleteUser(id);
      setUsers(users.filter(u => u.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete user';
      setError(message);
      throw err;
    }
  };

  const refreshUsers = async () => {
    await fetchUsers();
  };

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    deleteUser,
    refreshUsers,
  };
};

